import { useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ArrowLeft, Check, Copy, Loader2, X } from "lucide-react";

/**
 * DuplicateCleanupModal — three-step guided merge:
 *
 *   1. Groups list      → pick a duplicate group
 *   2. Master selection → pick which item survives
 *   3. Merge preview    → review the resulting merged record → confirm
 *
 * Presentation-only. Parent owns the merge execution (`onMerge`) and
 * duplicate detection. All fields are read via helper accessors so we
 * respect the CDI schema (`item_name` top-level + `item_data.*`).
 */
export default function DuplicateCleanupModal({
  open,
  onClose,
  groups,
  onMerge,
  saving,
}) {
  const [groupIdx, setGroupIdx] = useState(null);
  const [masterId, setMasterId] = useState(null);

  if (!open) return null;

  const selectedGroup = groupIdx !== null ? groups[groupIdx] : null;
  const selectedMaster = selectedGroup && masterId
    ? selectedGroup.find((it) => it.id === masterId)
    : null;

  const reset = () => { setGroupIdx(null); setMasterId(null); };
  const back = () => {
    if (selectedMaster) { setMasterId(null); return; }
    if (selectedGroup) { setGroupIdx(null); return; }
    onClose?.();
  };

  // Field accessors — the CDI schema keeps most non-name fields inside
  // item_data. We surface the five most useful ones (matching the spec)
  // in the preview, with graceful "—" fallbacks.
  const g = (it, key) => {
    if (!it) return "";
    if (key === "name") return it.item_name || "";
    return (it.item_data || {})[key] || "";
  };

  // Merge preview: master wins on every non-empty cell; otherwise the
  // first non-empty value across the rest of the group fills in.
  const previewMerged = (master, group, fields) => {
    const out = {};
    for (const f of fields) {
      const mv = g(master, f);
      if (mv) { out[f] = mv; continue; }
      const donor = group.find((it) => it.id !== master.id && g(it, f));
      out[f] = donor ? g(donor, f) : "";
    }
    return out;
  };

  const PREVIEW_FIELDS = ["name", "Vintage", "Region", "Notes", "Rating"];

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/55 backdrop-blur-sm vp-fade-in"
      data-testid="dup-cleanup-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dup-cleanup-title"
        className="w-full sm:max-w-lg bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-t-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Copy size={14} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
              <h2 id="dup-cleanup-title" className="text-base font-semibold tracking-tight">
                Duplicate Cleanup
              </h2>
            </div>
            <p className="text-xs vp-muted">
              {!selectedGroup && `${groups.length} duplicate group${groups.length === 1 ? "" : "s"} detected in the current view.`}
              {selectedGroup && !selectedMaster && "Pick the item to keep. All values from the others will fold into it."}
              {selectedGroup && selectedMaster && "Review the merged record. Duplicates will be deleted."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="dup-cleanup-close"
            aria-label="Close"
            className="vp-btn vp-btn-ghost vp-btn-sm !w-8 !h-8 !p-0 border border-[hsl(var(--border))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto">
          {!selectedGroup && (
            <ul className="space-y-2" data-testid="dup-cleanup-groups">
              {groups.map((group, i) => (
                <li key={i}>
                  <button
                    type="button"
                    data-testid={`dup-cleanup-group-${i}`}
                    onClick={() => setGroupIdx(i)}
                    className="w-full text-left border border-[hsl(var(--border))] rounded-md px-3 py-2.5 hover:bg-[hsl(var(--accent))] transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{group[0].item_name || "Untitled"}</div>
                        <div className="text-[11px] vp-muted">
                          {[g(group[0], "Vintage"), g(group[0], "Region")].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] shrink-0">
                        {group.length} items
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedGroup && !selectedMaster && (
            <ul className="space-y-2" data-testid="dup-cleanup-master-picker">
              {selectedGroup.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    data-testid={`dup-cleanup-master-${it.id}`}
                    onClick={() => setMasterId(it.id)}
                    className="w-full text-left border border-[hsl(var(--border))] rounded-md px-3 py-2.5 hover:bg-[hsl(var(--accent))] transition-colors"
                  >
                    <div className="font-medium text-sm truncate">{it.item_name || "Untitled"}</div>
                    <div className="text-[11px] vp-muted">
                      {PREVIEW_FIELDS.slice(1).map((f) => (
                        <span key={f} className="mr-3">
                          {f}: <span className="text-[hsl(var(--text))]">{g(it, f) || "—"}</span>
                        </span>
                      ))}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedGroup && selectedMaster && (
            <div data-testid="dup-cleanup-preview">
              <table className="w-full text-xs mb-3">
                <tbody className="divide-y divide-[hsl(var(--border))]">
                  {PREVIEW_FIELDS.map((f) => {
                    const merged = previewMerged(selectedMaster, selectedGroup, PREVIEW_FIELDS);
                    return (
                      <tr key={f}>
                        <td className="font-medium py-1.5 pr-3 vp-muted w-24">{f}</td>
                        <td className="py-1.5">{merged[f] || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-[11px] vp-muted italic">
                {selectedGroup.length - 1} duplicate{selectedGroup.length - 1 === 1 ? "" : "s"} will be deleted. The master
                keeps its ID; snapshots are recorded so this merge can be undone.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex justify-between gap-2">
          {selectedGroup ? (
            <button
              type="button"
              onClick={back}
              disabled={saving}
              data-testid="dup-cleanup-back"
              className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
            >
              <ArrowLeft size={13} /> Back
            </button>
          ) : <span />}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="dup-cleanup-cancel"
              className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
            >
              Cancel
            </button>
            {selectedGroup && selectedMaster && (
              <button
                type="button"
                onClick={() => {
                  onMerge?.(selectedMaster, selectedGroup);
                  reset();
                }}
                disabled={saving}
                data-testid="dup-cleanup-confirm"
                className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                {saving ? "Merging…" : "Merge Items"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}
